<main>
  <loading-overlay [isLoading]="loading()" />

  <div class="content">
    <div class="page__title">
      <div class="page__title_container">
        <div>
          <div class="row-start-center gap-3">
            <h1>{{ 'Cost_Analysis_Title' | translateWF: 'Cost Analysis' }}</h1>
            @if (periodSummary(); as ps) {
              <p-tag severity="secondary" [rounded]="true" icon="pi pi-calendar" [value]="(periodTranslationKey() | translateWF: '') + ' ' + ps.year" />
            }
          </div>
        </div>
        <div class="page__title_buttons">
          <p-button
            [icon]="valuesHidden() ? 'pi pi-eye' : 'pi pi-eye-slash'"
            severity="secondary"
            [outlined]="true"
            [pTooltip]="valuesHidden() ? ('Cost_Analysis_Show_Values' | translateWF: 'Show values') : ('Cost_Analysis_Hide_Values' | translateWF: 'Hide values')"
            tooltipPosition="bottom"
            [attr.aria-label]="valuesHidden() ? ('Cost_Analysis_Show_Values' | translateWF: 'Show values') : ('Cost_Analysis_Hide_Values' | translateWF: 'Hide values')"
            (onClick)="toggleValuesHidden()"
          />
          <p-button
            [label]="filtersCollapsed() ? ('Cost_Analysis_Show_Filters' | translateWF: 'Show filters') : ('Cost_Analysis_Hide_Filters' | translateWF: 'Hide filters')"
            [icon]="filtersCollapsed() ? 'pi pi-filter' : 'pi pi-filter-slash'"
            severity="secondary"
            [outlined]="true"
            [badge]="filtersCollapsed() && activeFilterCount() > 0 ? activeFilterCount().toString() : undefined"
            (onClick)="toggleFiltersCollapsed()"
          />
        </div>
      </div>
    </div>

    <div class="grid-12 gap-[1.5rem]">
      <aside class="span-12-12-3 col" [class.hidden]="filtersCollapsed()">
        @if (filterConfig()) {
          <csl-dynamic-filters [filterConfig]="filterConfig()!" [state]="filterState" (onApply)="handleApply()" (onReset)="handleReset()" />
        }

        <p-panel [header]="'Cost_Analysis_Filter_Scope' | translateWF: 'Narrow the report'" [toggleable]="true" [collapsed]="true">
          <ng-template pTemplate="icons">
            @if (activeFilterCount() > 0) {
              <p-tag [value]="activeFilterCount().toString()" [rounded]="true" severity="info" />
            }
          </ng-template>
          <p-card>
            <div class="col">
              <div class="form-box">
                <label>{{ 'Cost_Analysis_Filter_Fabric' | translateWF: 'Fabric' }}</label>
                <p-multiSelect
                  [formControl]="fabricFilterControl"
                  [options]="fabricFilterOptions()"
                  optionLabel="label"
                  optionValue="value"
                  [filter]="true"
                  [showClear]="true"
                  [placeholder]="'Cost_Analysis_Filter_Fabric' | translateWF: 'Fabric'"
                  display="chip"
                  styleClass="w-full"
                  appendTo="body"
                />
              </div>
              <div class="form-box">
                <label>{{ 'Cost_Analysis_Filter_Color' | translateWF: 'Color' }}</label>
                <p-multiSelect
                  [formControl]="colorFilterControl"
                  [options]="colorFilterOptions()"
                  optionLabel="label"
                  optionValue="value"
                  [filter]="true"
                  [showClear]="true"
                  [placeholder]="'Cost_Analysis_Filter_Color' | translateWF: 'Color'"
                  display="chip"
                  styleClass="w-full"
                  appendTo="body"
                  (onFilter)="onFilterAutocomplete('color', $event)"
                  (onShow)="onFilterAutocomplete('color', { filter: '' })"
                />
              </div>
              <div class="form-box">
                <label>{{ 'Cost_Analysis_Filter_Production_Card' | translateWF: 'Production Card' }}</label>
                <p-multiSelect
                  [formControl]="productionCardFilterControl"
                  [options]="productionCardFilterOptions()"
                  optionLabel="label"
                  optionValue="value"
                  [filter]="true"
                  [showClear]="true"
                  [placeholder]="'Cost_Analysis_Filter_Production_Card' | translateWF: 'Production Card'"
                  display="chip"
                  styleClass="w-full"
                  appendTo="body"
                  (onFilter)="onFilterAutocomplete('productionCard', $event)"
                  (onShow)="onFilterAutocomplete('productionCard', { filter: '' })"
                />
              </div>
              <div class="form-box">
                <label>{{ 'Cost_Analysis_Filter_Work_Order' | translateWF: 'Work Order' }}</label>
                <p-multiSelect
                  [formControl]="workOrderFilterControl"
                  [options]="workOrderFilterOptions()"
                  optionLabel="label"
                  optionValue="value"
                  [filter]="true"
                  [showClear]="true"
                  [placeholder]="'Cost_Analysis_Filter_Work_Order' | translateWF: 'Work Order'"
                  display="chip"
                  styleClass="w-full"
                  appendTo="body"
                  (onFilter)="onFilterAutocomplete('workOrder', $event)"
                  (onShow)="onFilterAutocomplete('workOrder', { filter: '' })"
                />
              </div>
              <div class="form-box">
                <label>{{ 'Cost_Analysis_Filter_Roll' | translateWF: 'Roll' }}</label>
                <p-multiSelect
                  [formControl]="rollFilterControl"
                  [options]="rollFilterOptions()"
                  optionLabel="label"
                  optionValue="value"
                  [filter]="true"
                  [showClear]="true"
                  [virtualScroll]="true"
                  [virtualScrollItemSize]="36"
                  [placeholder]="'Cost_Analysis_Filter_Roll' | translateWF: 'Roll'"
                  display="chip"
                  styleClass="w-full"
                  appendTo="body"
                  (onFilter)="onFilterAutocomplete('roll', $event)"
                  (onShow)="onFilterAutocomplete('roll', { filter: '' })"
                />
              </div>
            </div>
          </p-card>
        </p-panel>
      </aside>

      <section class="flex min-w-0 flex-col" [ngClass]="filtersCollapsed() ? 'span-12' : 'span-12-12-9'">
        @if (notCalculated() && !loading()) {
          <p-message severity="secondary" styleClass="w-full">
            <div class="col-sm">
              <span class="font-semibold">{{
                'Cost_Analysis_Not_Calculated'
                  | translateWF
                    : 'No cost calculated for this
              period.'
              }}</span>
              <span class="text-xs text-[var(--p-text-muted-color)]">
                {{ 'Cost_Analysis_Not_Calculated_Cta' | translateWF: 'Run the pools calculation from the Pools page.' }}
              </span>
            </div>
          </p-message>
        }

        @if (!loading() && hasData()) {
          <div class="grid-12 gap-[1.5rem]">
            <div class="span-12-4">
              <app-cost-kpi-card
                icon="pi pi-dollar"
                [label]="'Cost_Analysis_Average_Cost' | translateWF: 'Average Cost'"
                [value]="maskValue('$' + (realCostPerLbUsd() | number: '1.4-4'))"
                unit="USD / lb"
              />
            </div>
            <div class="span-12-4">
              <app-cost-kpi-card
                icon="pi pi-bolt"
                [label]="'Cost_Analysis_Standard_Cost' | translateWF: 'Standard Cost'"
                [value]="maskValue(standardCostPerLbUsd() > 0 ? '$' + (standardCostPerLbUsd() | number: '1.4-4') : '—')"
                unit="USD / lb"
              />
            </div>
            <div class="span-12-4">
              <app-cost-kpi-card
                icon="pi pi-chart-line"
                [label]="'Cost_Analysis_Variance' | translateWF: 'Variance (Real vs Standard)'"
                [value]="maskValue(variancePct() === null ? '—' : '$' + (varianceCostPerLbUsd() | number: '1.4-4'))"
                [unit]="valuesHidden() ? '' : variancePct() === null ? '' : (varianceCostPerLbUsd() > 0 ? '+' : '') + (variancePct() | number: '1.1-1') + '%'"
                [tone]="varianceTone()"
              />
            </div>
          </div>

          <div class="flex items-end justify-between border-b border-[var(--p-surface-200)]">
            <div class="row-start-center">
              <button type="button" class="ca-tab" [class.ca-tab--active]="activeTab() === 'by-process'" (click)="onTabNameChange('by-process')">
                {{ 'Cost_Analysis_Tab_ByProcess' | translateWF: 'By Process' }}
              </button>
              <button type="button" class="ca-tab" [class.ca-tab--active]="activeTab() === 'by-fabric'" (click)="onTabNameChange('by-fabric')">
                <span>{{ 'Cost_Analysis_Tab_ByFabric' | translateWF: 'By Fabric' }}</span>
                @if (fabricCount() !== null) {
                  <p-tag [value]="fabricCount()!.toString()" [rounded]="true" styleClass="ca-tab-count" />
                }
              </button>
            </div>

            @if (activeTab() === 'by-fabric' && fabricCount() !== null) {
              <button type="button" class="ca-expand-btn mb-2" (click)="toggleAllFabrics()">
                <i [class]="allFabricsExpanded() ? 'pi pi-chevron-up' : 'pi pi-chevron-down'"></i>
                <span>{{ allFabricsExpanded() ? ('Cost_Analysis_Collapse_All' | translateWF: 'Collapse all') : ('Cost_Analysis_Expand_All' | translateWF: 'Expand all') }}</span>
              </button>
            }
          </div>

          <cs-tabset type="brand" [activeIndex]="activeTabIndex()" [destroyOnHide]="false" (activeTabNameChange)="onTabNameChange($event)">
            <cs-tab name="by-process" [title]="'Cost_Analysis_Tab_ByProcess' | translateWF: 'By Process'">
              <ng-template #csTabContent>
                <app-process-summary
                  [year]="currentYear()"
                  [month]="currentMonth()"
                  [costSource]="stateCostSourceFilter()"
                  [productionLbs]="productionLbs()"
                  (processClick)="onProcessClick($event)"
                />
              </ng-template>
            </cs-tab>
            <cs-tab name="by-fabric" [title]="'Cost_Analysis_Tab_ByFabric' | translateWF: 'By Fabric'">
              <ng-template #csTabContent>
                <app-fabric-summary [year]="currentYear()" [month]="currentMonth()" [costSource]="stateCostSourceFilter()" (processClick)="onProcessClick($event)" />
              </ng-template>
            </cs-tab>
          </cs-tabset>
        }
      </section>
    </div>
  </div>
</main>
